import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReports from "@/pages/admin/AdminReports";
import { Locked } from "@/components/app/ui";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/site/Home";
import Platform from "@/pages/site/Platform";
import HowItWorks from "@/pages/site/HowItWorks";
import Integrations from "@/pages/site/Integrations";
import Pricing from "@/pages/site/Pricing";
import Resources from "@/pages/site/Resources";
import About from "@/pages/site/About";
import Contact from "@/pages/site/Contact";
import Tour from "@/pages/site/Tour";
import Legal from "@/pages/site/Legal";
import Checkout from "@/pages/site/Checkout";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import { ForgotPassword, ResetPassword } from "@/pages/auth/Password";
import AppLayout from "@/pages/app/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import Brand from "@/pages/app/Brand";
import Knowledge from "@/pages/app/Knowledge";
import Studio from "@/pages/app/Studio";
import CalendarPage from "@/pages/app/CalendarPage";
import Approvals from "@/pages/app/Approvals";
import Campaigns from "@/pages/app/Campaigns";
import Analytics from "@/pages/app/Analytics";
import Credits from "@/pages/app/Credits";
import AppIntegrations from "@/pages/app/AppIntegrations";
import Billing from "@/pages/app/Billing";
import SettingsPage from "@/pages/app/SettingsPage";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCompanies from "@/pages/admin/AdminCompanies";
import { AdminEnquiries, AdminPayments } from "@/pages/admin/AdminLists";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEvents from "@/pages/admin/AdminEvents";
import Leads from "@/pages/app/Leads";
import Intel from "@/pages/app/Intel";
import Personas from "@/pages/app/Personas";
import Events from "@/pages/app/Events";
import Digests from "@/pages/app/Digests";
import Team from "@/pages/app/Team";
import LeadForm from "@/pages/site/LeadForm";

const Loader = () => <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#7c3aed] border-t-transparent animate-spin" /></div>;

const Protected = ({ children, admin }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
};

const Gate = ({ module, feature, children }) => {
  const { company } = useAuth();
  return company?.modules?.includes(module) ? children : <Locked feature={feature} />;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem storageKey="sl_theme">
      <AuthProvider>
        <CurrencyProvider>
          <SiteContentProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/tour" element={<Tour />} />
                <Route path="/legal/:slug" element={<Legal />} />
                <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/f/:fid" element={<LeadForm />} />
              <Route element={<Protected><AppLayout /></Protected>}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/app/brand" element={<Brand />} />
                <Route path="/app/knowledge" element={<Knowledge />} />
                <Route path="/app/studio" element={<Gate module="content" feature="Content Studio"><Studio /></Gate>} />
                <Route path="/app/calendar" element={<Gate module="calendar" feature="Content Calendar"><CalendarPage /></Gate>} />
                <Route path="/app/approvals" element={<Gate module="approvals" feature="Approvals"><Approvals /></Gate>} />
                <Route path="/app/campaigns" element={<Gate module="campaigns" feature="Campaigns"><Campaigns /></Gate>} />
                <Route path="/app/analytics" element={<Gate module="analytics" feature="Analytics"><Analytics /></Gate>} />
                <Route path="/app/credits" element={<Credits />} />
                <Route path="/app/leads" element={<Gate module="leads" feature="Leads & Tracking"><Leads /></Gate>} />
                <Route path="/app/intel" element={<Gate module="intel" feature="Market Intelligence"><Intel /></Gate>} />
                <Route path="/app/personas" element={<Gate module="personas" feature="Customer Personas"><Personas /></Gate>} />
                <Route path="/app/events" element={<Gate module="events" feature="Industry Events"><Events /></Gate>} />
                <Route path="/app/digests" element={<Digests />} />
                <Route path="/app/team" element={<Gate module="team" feature="Team"><Team /></Gate>} />
                <Route path="/admin/events" element={<Protected admin><AdminEvents /></Protected>} />
                <Route path="/admin/cms" element={<Protected admin><AdminCMS /></Protected>} />
                <Route path="/admin/users" element={<Protected admin><AdminUsers /></Protected>} />
                <Route path="/admin/reports" element={<Protected admin><AdminReports /></Protected>} />
                <Route path="/app/integrations" element={<Gate module="integrations" feature="Integrations"><AppIntegrations /></Gate>} />
                <Route path="/app/billing" element={<Billing />} />
                <Route path="/app/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<Protected admin><AdminOverview /></Protected>} />
                <Route path="/admin/companies" element={<Protected admin><AdminCompanies /></Protected>} />
                <Route path="/admin/enquiries" element={<Protected admin><AdminEnquiries /></Protected>} />
                <Route path="/admin/payments" element={<Protected admin><AdminPayments /></Protected>} />
                <Route path="/admin/settings" element={<Protected admin><AdminSettings /></Protected>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
          </SiteContentProvider>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
